import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subscription, BehaviorSubject, combineLatest, of } from 'rxjs';
import { switchMap, filter, map, take } from 'rxjs/operators';

import { DiscoverRoutingService } from '../../../Discover/DiscoverRouter/services/discover-routing.service';
import { MonitoringRoutingService } from '../../../Monitor/Core/services/monitoring-routing.service';

import { DistributionSummaryModel } from '../../TargetingShared/model/distribution-summary-model';
import { DistributionService } from '../../TargetingShared/services/distribution.service';
import { DistributionStatus } from '../../TargetingShared/enum/distribution-status.enum';
import { TargetingResultModel } from '@Discover/Targeting/models/targeting-result.model';
import { TargetingList } from '@Discover/Targeting/models/targeting-list.model';
import { HttpErrorResponse } from '@angular/common/http';
import { TargetingListService } from '@Discover/Targeting/services/targeting-list.service';
import { TargetingSearchService } from '@Discover/Targeting/services/targeting-search.service';
import { DistributionApi } from '@api/distributions/distribution.api';
import { MessageService } from 'primeng/api';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { TargetingSearch } from '@Discover/Targeting/models/targeting-search.model';
import { EntityType } from '@api/targeting/enum/entity.type.enum';

@Injectable()
export class DistributionPickerService implements OnDestroy {
    public readonly MAX_CONTACTS = 10000;

    private distributions: DistributionSummaryModel[] = [];

    private workingDistributions: DistributionSummaryModel[] = [];
    private workingDistributions$: BehaviorSubject<DistributionSummaryModel[]> = new BehaviorSubject<DistributionSummaryModel[]>(this.workingDistributions);

    private readonly workingDistributionsSessionKey: string = 'targeting-working-distributions';
    private workingDistributionsLock: boolean = false;
    private workingDistributionsLock$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public useLocking: boolean = false;

    selectedEntities: TargetingResultModel[] = null;
    private selectedEntities$: BehaviorSubject<TargetingResultModel[]> = new BehaviorSubject<TargetingResultModel[]>(this.selectedEntities);

    allResultsSelected: boolean = false;
    total: number = 0;
    setDistributionFromUrlParam: string;
    private total$: BehaviorSubject<number> = new BehaviorSubject<number>(this.total);

    private listMembershipsChanged$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    private subscriptions: Subscription[] = [];

    constructor(
        private discoverRoutingService: DiscoverRoutingService,
        private monitoringRoutingService: MonitoringRoutingService,
        private distributionService: DistributionService,
        private localizationService: LocalizationService,
        private messageService: MessageService,
        private targetingListService: TargetingListService,
        private targetingSearchService: TargetingSearchService,
        private coreDialogService: CoreDialogService,
        private distributionApi: DistributionApi,
    ) {
        this.subscriptions.push(
            this.distributionService.getDistributionSummaries().pipe(filter(x => !!x)).subscribe(distros => {
                this.distributions = distros.filter(x => x.status === DistributionStatus.draft);
                if (this.setDistributionFromUrlParam) {
                    this.workingDistributionsLock = true;
                    const selectedDistribution = this.distributions.find(dist => dist.id === Number(this.setDistributionFromUrlParam));
                    this.setDistributionFromUrlParam = null;
                    this.setWorkingDistributions([selectedDistribution]);
                }
            }),
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    if (route.queryParamMap && route.queryParamMap.has('filters')) {
                        const filtersQueryString = route.queryParamMap.get('filters');
                        const model = TargetingSearch.fromFilterQueryString(filtersQueryString);
                        if (model?.distributionId) {
                            this.setDistributionFromUrlParam = model.distributionId;
                        }
                    } else if (!this.workingDistributionsLock) {
                        this.setWorkingDistributions([]);
                    }
                }
            ),
            this.monitoringRoutingService.getCurrentRoute().subscribe(
                route => {
                    if (!this.workingDistributionsLock) {
                        this.setWorkingDistributions([]);
                    }
                }
            )
        );
    }

    removeWorkingDistributionByIndex(index: number) {
        this.setWorkingDistributions(this.workingDistributions.filter((list, i) => i !== index));
        if (this.workingDistributions.length === 0) {
            this.setWorkingDistributionsLock(false);
        }
    }

    setWorkingDistributions(lists: DistributionSummaryModel[]): void {
        this.workingDistributions = lists;
        this.workingDistributions$.next(this.workingDistributions);
        if (this.useLocking) {
            this.updateLockedWorkingDistributions();
        }
    }

    private updateLockedWorkingDistributions(): void {
        if (this.workingDistributionsLock) {
            sessionStorage.setItem(this.workingDistributionsSessionKey, JSON.stringify(this.workingDistributions.map(list => list.id)));
        } else if (sessionStorage.getItem(this.workingDistributionsSessionKey)) {
            sessionStorage.removeItem(this.workingDistributionsSessionKey);
        }
    }

    public loadWorkingDistributionsFromSession(): void {
        const raw = sessionStorage.getItem(this.workingDistributionsSessionKey);
        if (raw) {
            let ids;
            try {
                ids = JSON.parse(raw);
            } catch (err) {
                // handled by the if statement below because it covers more cases
            }
            if (!ids || ids.length < 1) {
                this.clearWorkingDistributions();
                return;
            }

            // if the target list already exists, grab the list, we don't need to make another API call to get the list.. (as it was previously done)
            combineLatest([
                this.distributionService.getDistributionSummaries().pipe(
                    filter(x => !!x),
                    map(dist => dist.filter(element => element.status === DistributionStatus.draft))
                ),
            ]).pipe(
                // take(1) => we only need this to return 1 time.. not everytime we do targetingLists$.next
                take(1),
                map(([lists]) => {
                    // from the list of IDs we got from session, map it with the target list to be used as filter chips
                    const workingDistributions = ids.map(id => lists.find(list => list.id === id)).filter(list => list);
                    this.setWorkingDistributions(workingDistributions);
                    this.setWorkingDistributionsLock(true);
                })
            ).subscribe();
        }
    }

    clearWorkingDistributions() {
        this.workingDistributions = [];
        this.workingDistributions$.next(this.workingDistributions);
        if (this.useLocking) {
            this.workingDistributionsLock = false;
            this.workingDistributionsLock$.next(this.workingDistributionsLock);
            this.updateLockedWorkingDistributions();
        }
    }

    getWorkingDistributions(): Observable<DistributionSummaryModel[]> {
        return this.workingDistributions$;
    }

    getWorkingDistributionsLock(): Observable<boolean> {
        return this.workingDistributionsLock$;
    }

    toggleWorkingDistributionsLock() {
        this.setWorkingDistributionsLock(!this.workingDistributionsLock);
    }

    setWorkingDistributionsLock(locked: boolean): void {
        if (this.useLocking) {
            this.workingDistributionsLock = locked;
            this.workingDistributionsLock$.next(this.workingDistributionsLock);
            this.updateLockedWorkingDistributions();
        }
    }

    setSelectedEntities(selectedEntities: TargetingResultModel[]) {
        this.selectedEntities = selectedEntities;
        this.selectedEntities$.next(this.selectedEntities);
        if (!this.allResultsSelected) {
            this.total = selectedEntities.length;
            this.total$.next(this.total);
        }
    }

    addSelectedEntitiesToDists(dists: DistributionSummaryModel[]): Promise<void> {
        return this.addEntitiesToDists(this.selectedEntities, dists);
    }

    addSelectedEntitiesToWorkingDists(): Promise<void> {
        return this.addEntitiesToDists(this.selectedEntities, this.workingDistributions);
    }

    addEntitiesToDists(entities: TargetingResultModel[], dists: DistributionSummaryModel[]): Promise<void> {
        let apiCall;
        const total = this.total ? this.total : dists?.length;
        if (this.allResultsSelected) {
            if (this.total > this.MAX_CONTACTS) {
                return this.warnExceedingAddLimit();
            }

            apiCall = this.distributionApi.addSearchResultsToDist(
                dists.map(x => x.id),
                this.targetingSearchService.getCurrentSearchContext());

        } else {
            apiCall = this.distributionApi.addEntitiesToDist(
                dists.map(x => x.id),
                entities.map(x => x.toEntityIdModel())
            );
        }
        const isOutletOnly = entities.every(x => x.entityType === EntityType.OUTLET);

        return new Promise<void>((resolve => {
            apiCall.pipe(
                switchMap(() => {
                    return this.targetingListService.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                        map(x => null)
                    );
                }),
            ).subscribe({
                next: () => {
                    // To show the updated counts in the side bar
                    this.targetingSearchService.doSearch(true, true);

                    // If entities get removed from the list in another tab, then this could be inaccurate
                    entities.forEach(result => {
                        dists.forEach(list => {
                            if (result.listMemberships && !result.listMemberships.find(x => x.id === list.id)) {
                                result.listMemberships.push(new TargetingList({
                                    id: list.id,
                                    name: list.name
                                }));
                            }
                        });
                    });
                    this.listMembershipsChanged$.next(null);

                    // TODO : This should be clickable to navigate to the list
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Discover.distBuilder.addToDistSuccessMessageSummary' + (isOutletOnly ? '.outlet' : '')),
                        detail: dists.length === 1 ?
                            this.localizationService.transform('Discover.distBuilder.addToDistSuccessMessage' + (isOutletOnly ? '.outlet' : ''), {
                                count: total,
                                distName: dists[0].name
                            })
                            : this.localizationService.transform('Discover.distBuilder.addToDistsSuccessMessage' + (isOutletOnly ? '.outlet' : ''), {
                                contactCount: total,
                                distCount: dists.length
                            })
                    });
                    resolve();
                },
                error: (error) => {
                    if (error instanceof HttpErrorResponse) {
                        // TODO : coded error
                        if ((<string>error?.error)?.indexOf('size to exceed the maximum of') > 0) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                detail: 'Discover.listBuilder.error.maxSize'
                            });
                        } else {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Discover.distBuilder.error.addToDistMessageSummary' + (isOutletOnly ? '.outlet' : ''),
                                detail: 'Discover.distBuilder.error.addToDistMessage' + (isOutletOnly ? '.outlet': '')
                            });
                        }
                    }
                    resolve();
                }
            });
        }));
    }

    warnExceedingAddLimit(): Promise<void> {
        return new Promise<void>(resolve => {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    alertType: messageSeverityEnum.warning,
                    heading: this.localizationService.transform('Discover.listBuilder.errorDialog.addToList.header'),
                    message: this.localizationService.transform('Discover.listBuilder.errorDialog.addToList.message'),
                    alertMessage: this.localizationService.transform('Discover.listBuilder.errorDialog.addToList.warning'),
                    alertMessageList: [
                        this.localizationService.transform(
                            'Discover.listBuilder.errorDialog.addToList.warningMessage',
                            { contacts: this.total }
                        )
                    ],
                    okLabel: this.localizationService.transform('Discover.listBuilder.errorDialog.addToList.acknowledge'),
                    showCancel: false,
                    confirmAction: () => {
                        resolve();
                        return of(true);
                    }
                }
            });
        });
    }

    setAllResultsSelected(allResultsSelected: boolean, total: number = 0) {
        this.allResultsSelected = allResultsSelected;
        this.total = total;
        this.total$.next(this.total);
    }

    getSelectedEntities(): Observable<TargetingResultModel[]> {
        return this.selectedEntities$;
    }

    getTotal(): Observable<number> {
        return this.total$;
    }

    getListMembershipsChanged() {
        return this.listMembershipsChanged$;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
